import React from "react";
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  SimpleGrid,
  Stack,
  Button,
  List,
  ListItem,
  ListIcon,
  useColorModeValue,
  Badge,
  Icon,
  Flex,
} from "@chakra-ui/react";
import {
  FaCheck,
  FaBook,
  FaPencilAlt,
  FaChartBar,
  FaUserGraduate,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { useLanguage } from "../contexts/LanguageContext";
import SEO from "../components/SEO";
import { seoData } from "../utils/seoData";

const ProductCard = ({
  title,
  description,
  icon,
  features,
  badge,
  path,
  buttonLabel,
}) => {
  const navigate = useNavigate();

  return (
    <Stack
      bg={useColorModeValue("white", "gray.800")}
      p={10}
      rounded="3xl"
      shadow="xl"
      position="relative"
      transition="all 0.4s"
      _hover={{ transform: "translateY(-8px)", shadow: "2xl" }}
      border="1px"
      borderColor={useColorModeValue("gray.100", "gray.700")}
    >
      {badge && (
        <Badge
          position="absolute"
          top={6}
          right={6}
          bgGradient="linear(to-r, #FF6A5F, #A63FF5)"
          color="white"
          fontSize="md"
          px={4}
          py={2}
          rounded="full"
          fontWeight="bold"
        >
          {badge}
        </Badge>
      )}

      <Flex
        w={20}
        h={20}
        align="center"
        justify="center"
        color="white"
        rounded="2xl"
        bgGradient="linear(to-br, #FF6A5F, #A63FF5)"
        mb={6}
      >
        {icon}
      </Flex>

      <Heading size="xl" mb={4}>
        {title}
      </Heading>

      <Text color="gray.600" mb={8} fontSize="md" lineHeight="tall">
        {description}
      </Text>

      <List spacing={4} mb={8}>
        {features.map((feature, index) => (
          <ListItem key={index}>
            <ListIcon as={FaCheck} color="#A63FF5" w={5} h={5} />
            {feature}
          </ListItem>
        ))}
      </List>

      <Button
        size="lg"
        h="56px"
        mt="auto"
        bgGradient="linear(to-r, #FF6A5F, #A63FF5)"
        color="white"
        rounded="full"
        fontSize="lg"
        _hover={{
          bgGradient: "linear(to-r, #FF8FA5, #C56CFF)",
          transform: "translateY(-2px)",
          shadow: "xl",
        }}
        transition="all 0.3s"
        onClick={() => navigate(path)}
      >
        {buttonLabel}
      </Button>
    </Stack>
  );
};

const PricingCard = ({
  title,
  price,
  features,
  isPopular,
  buttonLabel,
  billingSuffix,
  popularLabel,
  onClick,
}) => {
  const navigate = useNavigate();
  const bgColor = useColorModeValue("white", "gray.800");
  const borderColorDefault = useColorModeValue("gray.200", "gray.700");

  return (
    <Stack
      bg={bgColor}
      p={10}
      rounded="3xl"
      shadow={isPopular ? "2xl" : "xl"}
      border={isPopular ? "2px" : "1px"}
      borderColor={isPopular ? "#A63FF5" : borderColorDefault}
      position="relative"
      transform={isPopular ? "scale(1.05)" : "scale(1)"}
      transition="all 0.4s"
      _hover={{
        transform: isPopular ? "scale(1.07)" : "scale(1.02)",
        shadow: "2xl",
      }}
    >
      {isPopular && (
        <Badge
          position="absolute"
          top={-4}
          left="50%"
          transform="translateX(-50%)"
          bgGradient="linear(to-r, #FF6A5F, #A63FF5)"
          color="white"
          fontSize="md"
          px={6}
          py={2}
          rounded="full"
          fontWeight="bold"
        >
          {popularLabel}
        </Badge>
      )}

      <Heading size="xl" mb={4}>
        {title}
      </Heading>

      <Flex align="baseline" mb={8}>
        <Heading
          fontSize={{ base: "4xl", md: "5xl" }}
          fontWeight="bold"
          bgGradient="linear(to-r, #FF6A5F, #A63FF5)"
          bgClip="text"
        >
          ${price}
        </Heading>
        <Text color="gray.600" ml={2} fontSize="lg">
          {billingSuffix}
        </Text>
      </Flex>

      <List spacing={4} mb={8}>
        {features.map((feature, index) => (
          <ListItem key={index}>
            <ListIcon as={FaCheck} color="#A63FF5" w={5} h={5} />
            {feature}
          </ListItem>
        ))}
      </List>

      <Button
        size="lg"
        h="56px"
        mt="auto"
        bgGradient={isPopular ? "linear(to-r, #FF6A5F, #A63FF5)" : "gray.200"}
        color={isPopular ? "white" : "gray.800"}
        rounded="full"
        fontSize="lg"
        _hover={{
          bgGradient: isPopular ? "linear(to-r, #FF8FA5, #C56CFF)" : "gray.300",
          transform: "translateY(-2px)",
          shadow: "xl",
        }}
        transition="all 0.3s"
        onClick={() => (onClick ? onClick() : navigate("/contact"))}
      >
        {buttonLabel}
      </Button>
    </Stack>
  );
};

const Products = () => {
  const { language } = useLanguage();
  const seo = seoData.products[language] || seoData.products.en;

  const translations = {
    en: {
      heroTitle: "Our Products",
      heroDescription:
        "Comprehensive solutions designed to transform how you create, manage, and deliver educational content",
      productSuiteTitle: "Product Suite",
      productSuiteDescription:
        "Everything you need to deliver exceptional learning experiences",
      productButtonLabel: "Learn More",
      productCards: [
        {
          icon: FaPencilAlt,
          title: "Circuit Training Program",
          description:
            "Structured practice program with rotating exercises designed to build comprehensive problem-solving skills.",
          badge: "Popular",
          path: "/products/circuit-training",
          features: [
            "Rotating practice stations",
            "Progressive difficulty levels",
            "Time-based circuit sessions",
            "Personalized training paths",
            "Performance tracking",
          ],
        },
        {
          icon: FaBook,
          title: "Problem Bank Manager",
          description:
            "Organize and manage your question banks with powerful search, tagging, and categorization tools.",
          path: "/products/problem-bank",
          features: [
            "Unlimited problem storage",
            "Advanced search & filters",
            "Custom categorization",
            "Version control",
            "Export to multiple formats",
          ],
        },
        {
          icon: FaChartBar,
          title: "Analytics Dashboard",
          description:
            "Track student performance and gain insights with comprehensive analytics and reporting tools.",
          badge: "New",
          path: "/products/analytics-dashboard",
          features: [
            "Real-time performance tracking",
            "Custom reports generation",
            "Progress visualization",
            "Predictive analytics",
            "Export & share reports",
          ],
        },
        {
          icon: FaUserGraduate,
          title: "Student Management",
          description:
            "Efficiently manage students, assign tasks, and track progress all in one centralized platform.",
          path: "/products/student-management",
          features: [
            "Student profile management",
            "Task assignment & tracking",
            "Progress monitoring",
            "Bulk operations support",
            "Parent/guardian access",
          ],
        },
      ],
      pricingTitle: "Pricing Plans",
      pricingDescription: "Choose the plan that's right for you",
      pricingButtonLabel: "Get Started",
      pricingBillingSuffix: "/month",
      pricingPopularLabel: "Most Popular",
      pricingPlans: [
        {
          title: "Starter",
          price: "29",
          features: [
            "Up to 100 students",
            "500 questions/month",
            "Basic analytics",
            "Email support",
            "5 GB storage",
          ],
        },
        {
          title: "Professional",
          price: "99",
          isPopular: true,
          features: [
            "Up to 500 students",
            "Unlimited questions",
            "Advanced analytics",
            "Priority support",
            "50 GB storage",
            "API access",
          ],
        },
        {
          title: "Enterprise",
          price: "299",
          features: [
            "Unlimited students",
            "Unlimited questions",
            "Custom analytics",
            "24/7 dedicated support",
            "Unlimited storage",
            "Custom integrations",
            "White-label option",
          ],
        },
      ],
      plansTitle: "All Plans Include",
      plansItems: [
        { title: "Secure & Reliable", description: "99.9% uptime guarantee" },
        { title: "Regular Updates", description: "New features every month" },
        { title: "Training & Onboarding", description: "Get started quickly" },
      ],
    },
    ko: {
      heroTitle: "제품 소개",
      heroDescription:
        "교육 콘텐츠를 만들고 관리하고 전달하는 방식을 혁신하도록 설계된 종합 솔루션입니다.",
      productSuiteTitle: "제품 구성",
      productSuiteDescription:
        "탁월한 학습 경험을 제공하는 데 필요한 모든 기능을 담았습니다.",
      productButtonLabel: "자세히 보기",
      productCards: [
        {
          icon: FaPencilAlt,
          title: "서킷 트레이닝 프로그램",
          description:
            "회전식 훈련으로 구성된 체계적인 연습 프로그램으로 종합적인 문제 해결 능력을 키워 줍니다.",
          badge: "인기",
          path: "/products/circuit-training",
          features: [
            "회전형 연습 스테이션",
            "난이도 단계별 상승",
            "시간 기반 서킷 세션",
            "학습자 맞춤 훈련 경로",
            "성과 분석 및 추적",
          ],
        },
        {
          icon: FaBook,
          title: "문제은행 매니저",
          description:
            "강력한 검색, 태그, 분류 기능으로 문제은행을 체계적으로 관리하세요.",
          path: "/products/problem-bank",
          features: [
            "무제한 문제 저장",
            "고급 검색 및 필터",
            "맞춤형 분류 체계",
            "버전 관리 기능",
            "다양한 형식으로 내보내기",
          ],
        },
        {
          icon: FaChartBar,
          title: "분석 대시보드",
          description:
            "종합적인 분석과 보고 기능으로 학습 성과를 추적하고 인사이트를 확보하세요.",
          badge: "신규",
          path: "/products/analytics-dashboard",
          features: [
            "실시간 성과 추적",
            "맞춤형 보고서 생성",
            "진행 상황 시각화",
            "예측 분석",
            "보고서 내보내기 및 공유",
          ],
        },
        {
          icon: FaUserGraduate,
          title: "학생 관리",
          description:
            "학생 관리, 과제 배포, 성과 추적을 하나의 플랫폼에서 효율적으로 처리하세요.",
          path: "/products/student-management",
          features: [
            "학생 프로필 관리",
            "과제 배포 및 추적",
            "진행 상황 모니터링",
            "대량 작업 지원",
            "학부모/보호자 접근 권한",
          ],
        },
      ],
      pricingTitle: "요금제",
      pricingDescription: "목적에 맞는 요금제를 선택하세요",
      pricingButtonLabel: "시작하기",
      pricingBillingSuffix: "/월",
      pricingPopularLabel: "가장 인기 있음",
      pricingPlans: [
        {
          title: "Starter",
          price: "29",
          features: [
            "최대 100명 학생 관리",
            "월 500문항 제공",
            "기본 분석 기능",
            "이메일 지원",
            "5GB 저장 공간",
          ],
        },
        {
          title: "Professional",
          price: "99",
          isPopular: true,
          features: [
            "최대 500명 학생 관리",
            "무제한 문항 제공",
            "고급 분석 기능",
            "우선 지원",
            "50GB 저장 공간",
            "API 접근",
          ],
        },
        {
          title: "Enterprise",
          price: "299",
          features: [
            "무제한 학생 관리",
            "무제한 문항 제공",
            "맞춤형 분석",
            "24/7 전담 지원",
            "무제한 저장 공간",
            "맞춤형 통합",
            "화이트 라벨 옵션",
          ],
        },
      ],
      plansTitle: "모든 요금제에 포함",
      plansItems: [
        { title: "안정성과 보안", description: "99.9% 가동률 보장" },
        { title: "정기 업데이트", description: "매달 새로운 기능 제공" },
        { title: "교육 및 온보딩", description: "빠른 시작을 위한 지원" },
      ],
    },
  };

  const t = translations[language] || translations.en;
  return (
    <>
      <SEO
        title={seo.title}
        description={seo.description}
        keywords={seo.keywords}
        canonical="/products"
        lang={language}
      />
      <Box>
        {/* Hero Section */}
        <Box
          bgGradient="linear(to-br, rgba(255, 106, 95, 0.05), rgba(166, 63, 245, 0.05))"
          py={{ base: 20, md: 28 }}
        >
          <Container maxW="container.xl">
            <VStack spacing={8} textAlign="center">
              <Heading
                fontSize={{ base: "4xl", md: "5xl", lg: "6xl" }}
                fontWeight="bold"
              >
                {t.heroTitle}
              </Heading>
              <Text
                fontSize={{ base: "lg", md: "xl" }}
                color="gray.600"
                maxW="900px"
                lineHeight="tall"
              >
                {t.heroDescription}
              </Text>
            </VStack>
          </Container>
        </Box>

        {/* Products Section */}
        <Box py={24}>
          <Container maxW="container.xl">
            <VStack spacing={16}>
              <VStack spacing={6} textAlign="center">
                <Heading
                  fontSize={{ base: "3xl", md: "4xl" }}
                  fontWeight="bold"
                >
                  {t.productSuiteTitle}
                </Heading>
                <Text
                  fontSize="xl"
                  color="gray.600"
                  maxW="700px"
                  lineHeight="tall"
                >
                  {t.productSuiteDescription}
                </Text>
              </VStack>

              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={10} w="full">
                {t.productCards.map((product, index) => (
                  <ProductCard
                    key={product.title}
                    icon={<Icon as={product.icon} w={10} h={10} />}
                    title={product.title}
                    description={product.description}
                    badge={product.badge}
                    path={product.path}
                    features={product.features}
                    buttonLabel={t.productButtonLabel}
                  />
                ))}
              </SimpleGrid>
            </VStack>
          </Container>
        </Box>

        {/* Pricing Section */}
        <Box py={24} bg={useColorModeValue("gray.50", "gray.900")}>
          <Container maxW="container.xl">
            <VStack spacing={16}>
              <VStack spacing={6} textAlign="center">
                <Heading
                  fontSize={{ base: "3xl", md: "4xl" }}
                  fontWeight="bold"
                >
                  {t.pricingTitle}
                </Heading>
                <Text
                  fontSize="xl"
                  color="gray.600"
                  maxW="700px"
                  lineHeight="tall"
                >
                  {t.pricingDescription}
                </Text>
              </VStack>

              <SimpleGrid
                columns={{ base: 1, md: 3 }}
                spacing={10}
                w="full"
                pt={4}
              >
                {t.pricingPlans.map((plan) => (
                  <PricingCard
                    key={plan.title}
                    title={plan.title}
                    price={plan.price}
                    features={plan.features}
                    isPopular={plan.isPopular}
                    buttonLabel={t.pricingButtonLabel}
                    billingSuffix={t.pricingBillingSuffix}
                    popularLabel={t.pricingPopularLabel}
                  />
                ))}
              </SimpleGrid>
            </VStack>
          </Container>
        </Box>

        {/* Features Comparison */}
        <Box py={24}>
          <Container maxW="container.xl">
            <VStack spacing={12} textAlign="center">
              <Heading fontSize={{ base: "3xl", md: "4xl" }} fontWeight="bold">
                {t.plansTitle}
              </Heading>
              <SimpleGrid columns={{ base: 1, md: 3 }} spacing={12} w="full">
                {t.plansItems.map((item, index) => (
                  <VStack key={index} spacing={4}>
                    <Flex
                      w={16}
                      h={16}
                      align="center"
                      justify="center"
                      rounded="full"
                      bg="rgba(166, 63, 245, 0.1)"
                    >
                      <Icon as={FaCheck} w={8} h={8} color="#A63FF5" />
                    </Flex>
                    <Text fontWeight="bold" fontSize="xl">
                      {item.title}
                    </Text>
                    <Text color="gray.600" fontSize="md">
                      {item.description}
                    </Text>
                  </VStack>
                ))}
              </SimpleGrid>
            </VStack>
          </Container>
        </Box>
      </Box>
    </>
  );
};

export default Products;
